import type { CaseInputs, DriverType, Recipe, View } from '../engine/types';

export type CaseFileValues =
    | (number | null)[]
    | number
    | Record<string, number | null>
    | Recipe;

export interface CaseFileLineHistory {
    months: string[];
    values: (number | null)[];
}

export interface CaseFileHistorySummary {
    unit: string;
    last_actual: { month: string; value: number; usd?: number } | null;
    trailing_avg: { '3': number; '6': number; '12': number };
    same_month_last_year: Record<string, number>;
    min_max_12: { min: number; max: number };
}

export interface CaseFileLine {
    label: string;
    unit: string;
    allowed_drivers: string[];
    driver: string | null;
    values: CaseFileValues;
    params: Record<string, unknown>;
    history?: CaseFileLineHistory;
    history_summary?: CaseFileHistorySummary;
    ignore_group_members?: string[];
}

export interface CaseFileReferenceBlock {
    river_oaks?: Record<string, CaseFileLineHistory>;
    highland_park?: Record<string, CaseFileLineHistory>;
    ga?: Record<string, CaseFileLineHistory>;
    consolidated?: Record<string, CaseFileLineHistory>;
    seasonality?: Record<string, Record<string, Record<string, number>>>;
    seasonality_notes?: Record<string, string>;
}

export interface CaseFileCashPolicy {
    enabled: boolean;
    floor: {
        operating_minimum: number | null;
        drawdown_allowance: number | null;
    };
    distribution: {
        cadence: 'biweekly' | 'monthly' | null;
        anchor_date: string | null;
        amount_per_period: number | null;
        one_time?: { date: string; amount: number } | null;
    };
    sweep: {
        enabled: boolean;
        mode: 'excess' | 'fixed';
        amount_per_period: number | null;
        yield_income_view: View | null;
        opening_mm_balance: number | null;
    };
    solver: {
        horizon_months: 12 | 24 | 36;
        round_to: 500 | 1000 | 5000;
    };
}

export interface CaseFileV1 {
    format: 'bookkeeper.projection_case';
    format_version: 1 | 2;
    _guide?: Record<string, unknown>;
    _reference?: CaseFileReferenceBlock;
    case: {
        name: string;
        description?: string;
        expires_on: string | null;
    };
    basis: {
        last_closed_month: string;
        months: string[];
        schema_fingerprint: string;
    };
    consolidated_only: string[];
    lines: Record<View, Record<string, CaseFileLine>>;
    one_time_items: CaseFileOneTimeItem[];
    cash_policy: CaseFileCashPolicy;
    notes: Record<string, string>;
}

export interface CaseFileOneTimeItem {
    month: string;
    view: View;
    line_key: string;
    amount: number;
    mode: 'incremental' | 'already_in_driver';
    notes?: string;
    id?: string;
}

export const FILE_ISSUE_CODES = {
    file_not_json: 'file_not_json',
    file_too_large: 'file_too_large',
    file_wrong_format: 'file_wrong_format',
    file_unsupported_version: 'file_unsupported_version',
    file_unknown_key: 'file_unknown_key',
    file_name_missing: 'file_name_missing',
    file_name_case_conflict: 'file_name_case_conflict',
    file_basis_ahead: 'file_basis_ahead',
    file_basis_too_old: 'file_basis_too_old',
    file_basis_months_mismatch: 'file_basis_months_mismatch',
    file_basis_behind: 'file_basis_behind',
    file_schema_fingerprint: 'file_schema_fingerprint',
    file_unknown_view: 'file_unknown_view',
    file_unknown_line: 'file_unknown_line',
    file_retired_line: 'file_retired_line',
    file_line_not_drivable: 'file_line_not_drivable',
    file_label_mismatch: 'file_label_mismatch',
    file_driver_not_allowed: 'file_driver_not_allowed',
    file_ignore_group_partial: 'file_ignore_group_partial',
    file_consolidated_only_conflict: 'file_consolidated_only_conflict',
    file_values_shape: 'file_values_shape',
    file_params_invalid: 'file_params_invalid',
    file_pct_of_line_cycle: 'file_pct_of_line_cycle',
    file_percent_scale: 'file_percent_scale',
    file_values_ignored: 'file_values_ignored',
    file_one_time_invalid: 'file_one_time_invalid',
    file_cash_policy_invalid: 'file_cash_policy_invalid',
    file_cash_policy_server_field: 'file_cash_policy_server_field',
    file_note_key_invalid: 'file_note_key_invalid',
    file_target_expired: 'file_target_expired',
    file_rationale_missing: 'file_rationale_missing',
    file_recipe_not_allowed: 'file_recipe_not_allowed',
    file_recipe_basis_unsupported: 'file_recipe_basis_unsupported',
    file_rationale_long: 'file_rationale_long',
    file_recipe_percent_scale: 'file_recipe_percent_scale',
    file_recipe_seasonality_history: 'file_recipe_seasonality_history',
    recipe_anchor_drift: 'recipe_anchor_drift',
} as const;

export type FileIssueCode = (typeof FILE_ISSUE_CODES)[keyof typeof FILE_ISSUE_CODES];

export interface FileIssue {
    code: FileIssueCode;
    severity: 'error' | 'warning';
    path: string;
    message: string;
}

export interface CaseFileSummary {
    drivenLinesByView: Record<View, number>;
    driverCounts: Record<string, number>;
    recipeLineCount: number;
    overrideCount: number;
    oneTimeItemCount: number;
    cashPolicyEnabled: boolean;
    noteCount: number;
    undrivenTemplateLineCount: number;
    rebaseDroppedOneTimeItems?: number;
    rebaseDroppedNotes?: number;
    rebaseRecipesRematerialised?: number;
}

export type ParsedCaseTarget =
    | { kind: 'create' }
    | { kind: 'override'; name: string }
    | { kind: 'blocked' };

export interface ParsedCaseFile {
    file: CaseFileV1 | null;
    inputs: CaseInputs | null;
    meta: { description?: string; expires_on?: string | null };
    notes: Record<string, string>;
    rebase: { needed: boolean; months: number } | null;
    target: ParsedCaseTarget;
    issues: FileIssue[];
    summary: CaseFileSummary | null;
}

export const FILE_VIEWS: readonly View[] = [
    'river_oaks',
    'highland_park',
    'ga',
    'consolidated',
];

export const PERCENT_DRIVERS = new Set<DriverType>([
    'mom_pct',
    'yoy_pct',
    'pct_gross_sales',
    'pct_gross_sales_growth',
    'pct_of_line',
]);

export type ExistingCaseInfo = {
    name: string;
    expires_on: string | null;
    expired: boolean;
};
